import { useRef, useEffect, useState, useCallback } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { Input } from "@/components/ui/input";
import { MapPin } from "lucide-react";

/**
 * Extracts Australian address components from Google Places result
 */
function extractAustralianAddressComponents(addressComponents, place) {
  let streetNumber = "";
  let route = "";
  let suburb = "";
  let state = "";
  let postcode = "";

  for (const component of addressComponents) {
    const types = component.types;

    if (types.includes("street_number")) {
      streetNumber = component.long_name;
    } else if (types.includes("route")) {
      route = component.long_name;
    } else if (types.includes("locality")) {
      suburb = component.long_name;
    } else if (types.includes("administrative_area_level_1")) {
      // Returns state abbreviation like NSW, VIC, QLD
      state = component.short_name;
    } else if (types.includes("postal_code")) {
      postcode = component.long_name;
    }
  }

  // Combine street number and route for full street address
  const streetAddress = streetNumber ? `${streetNumber} ${route}` : route;

  return {
    streetAddress: streetAddress.trim(),
    suburb,
    state,
    postcode,
    formattedAddress: place.formatted_address || "",
    placeId: place.place_id || "",
  };
}

/**
 * AddressAutocomplete Component
 * 
 * A reusable Google Places autocomplete input for Australian addresses.
 * Automatically extracts and returns structured address components.
 * 
 * @param {Object} props
 * @param {string} props.value - Current input value
 * @param {function} props.onChange - Called when input value changes
 * @param {function} props.onAddressSelect - Called with extracted address components when user selects an address
 * @param {string} props.placeholder - Input placeholder text
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.id - Input element ID
 * @param {Object} props.inputProps - Additional props to pass to the input element
 */
export function AddressAutocomplete({
  value,
  onChange,
  onAddressSelect,
  placeholder = "Start typing an address...",
  className = "",
  id,
  ...inputProps
}) {
  const inputRef = useRef(null);
  const places = useMapsLibrary("places");
  const [autocomplete, setAutocomplete] = useState(null);

  // Initialize autocomplete when places library loads
  useEffect(() => {
    if (!places || !inputRef.current) return;

    // Configure autocomplete options for Australian addresses
    const options = {
      fields: ["address_components", "geometry", "formatted_address", "place_id", "name"],
      componentRestrictions: { country: "au" },
      types: ["geocode", "establishment"],
    };

    const autocompleteInstance = new places.Autocomplete(inputRef.current, options);
    setAutocomplete(autocompleteInstance);

    return () => {
      // Cleanup - Google handles this internally
    };
  }, [places]);

  // Handle place selection
  const handlePlaceChanged = useCallback(() => {
    if (!autocomplete) return;

    const place = autocomplete.getPlace();

    if (!place.address_components) {
      console.warn("No address components available for selected place");
      return;
    }

    // Extract Australian address components
    const addressData = extractAustralianAddressComponents(
      place.address_components,
      place
    );

    // Update input value with formatted address
    if (onChange) {
      onChange(addressData.streetAddress || place.formatted_address || "");
    }

    // Call callback with extracted address data
    if (onAddressSelect) {
      onAddressSelect(addressData);
    }
  }, [autocomplete, onChange, onAddressSelect]);

  // Add place_changed listener
  useEffect(() => {
    if (!autocomplete) return;

    const listener = autocomplete.addListener("place_changed", handlePlaceChanged);

    return () => {
      if (listener) {
        window.google?.maps?.event?.removeListener(listener);
      }
    };
  }, [autocomplete, handlePlaceChanged]);

  return (
    <div className="relative">
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
      <Input
        ref={inputRef}
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange && onChange(e.target.value)}
        placeholder={placeholder}
        className={`pl-9 ${className}`}
        autoComplete="off"
        {...inputProps}
      />
    </div>
  );
}

export default AddressAutocomplete;
